import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  output,
  Output,
  Signal,
  viewChild,
  ViewChild
} from '@angular/core';

import { Actions, Store } from '@ngxs/store';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDateStruct,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import {
  DateFormatterService,
  DateUtilsService,
  InventoryManagementAuctionItemStatus,
  InventoryManagementSearchCriteria,
  InventoryManagementState,
  LaunchDarklyService
} from 'remarketing-angular-library';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { TrackDirective } from 'remarketing-ui-components-lib';
import { SearchFilterService } from '../../services/search-filter/search-filter.service';
import { takeUntil } from 'rxjs/operators';
import { SaComponent } from 'smartauction-frontend-micro-library';

@Component({
  selector: 'app-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  providers: [{ provide: NgbDateParserFormatter, useClass: DateFormatterService }],
  imports: [TranslocoDirective, ReactiveFormsModule, NgbInputDatepicker, TrackDirective]
})
export class DateRangeComponent extends SaComponent implements OnInit {
  fromCalendarButton = viewChild<ElementRef>('fromCalendar');
  toCalendarButton = viewChild<ElementRef>('toCalendar');
  hasDateRangeError = output<boolean>();
  trackCalendars = input<boolean>(false);

  protected fromFormControl = new UntypedFormControl();
  protected toFormControl = new UntypedFormControl();

  protected fromMinDate: NgbDateStruct;
  protected fromMaxDate: NgbDateStruct;
  protected toMaxDate: NgbDateStruct;
  protected toMinDate: NgbDateStruct;
  protected toStartDate: NgbDateStruct;
  protected fromStartDate: NgbDateStruct;
  private today: NgbDateStruct;

  protected fromDateValid = true;
  protected toDateValid = true;

  protected displayFromDate: string;
  protected displayToDate: string;

  private readonly store: Store;
  private readonly ngbCalendar: NgbCalendar;
  protected readonly dateFormatter: DateFormatterService;
  private readonly dateUtilsService: DateUtilsService;
  private readonly launchDarklyService: LaunchDarklyService;
  private readonly searchFilterService: SearchFilterService;

  get fromDateTrack() {
    return this.trackCalendars()
      ? DateRangeComponent.calendarTrackData('From Date')
      : { input: {}, button: {} };
  }

  get toDateTrack() {
    return this.trackCalendars() ? DateRangeComponent.calendarTrackData('To Date') : { input: {}, button: {} };
  }

  get isCanadianTenant() {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  constructor( ) {
    super();
    this.store = inject(Store);
    this.ngbCalendar = inject(NgbCalendar);
    this.dateFormatter = inject(DateFormatterService);
    this.dateUtilsService = inject(DateUtilsService);
    this.launchDarklyService = inject(LaunchDarklyService);
    this.searchFilterService = inject(SearchFilterService);
  }

  private static calendarTrackData(name: string) {
    return {
      input: { name: `${name}`, elem: 'calendar' },
      button: { name: `${name}`, elem: 'calendar' }
    };
  }

  ngOnInit() {
    this.setToday();
    this.setInitialValues();
  }

  setToday() {
    this.today = {
      year: this.ngbCalendar.getToday().year,
      month: this.ngbCalendar.getToday().month,
      day: this.ngbCalendar.getToday().day
    };
  }

  // Handles the logic for setting minimum and maximum allowed date values for the date picker
  setMinMaxDateValues(updatedFromDate: NgbDateStruct | undefined, searchCriteria: InventoryManagementSearchCriteria) {
    const maxFromDate: number = this.launchDarklyService.getFeatureFlag('sell-removedVehicleMaxFromDays', 30);
    // Removed Vehicle Search includes a maxFromDate value, which equates to the fromMinDate here (earliest valid date)
    if (searchCriteria && searchCriteria.filterByStatus === InventoryManagementAuctionItemStatus.REMOVED) {
      const minDate = this.ngbCalendar.getPrev(
        { year: this.today.year, month: this.today.month, day: this.today.day } as NgbDate,
        'd',
        maxFromDate
      );
      this.fromMinDate = { year: minDate.year, month: minDate.month, day: minDate.day };
    } else {
      this.fromMinDate = { year: this.today.year - 1, month: this.today.month, day: this.today.day };
    }

    this.toMinDate = updatedFromDate ? updatedFromDate : this.fromMinDate;
  }

  private calculateDates(searchCriteria: InventoryManagementSearchCriteria) {
    const updatedFromDate = this.getFromDate(searchCriteria);
    const updatedToDate = this.getToDate(searchCriteria);

    this.toFormControl.setValue(updatedToDate, { emitEvent: false });
    this.fromFormControl.setValue(updatedFromDate, { emitEvent: false });

    this.fromStartDate = updatedFromDate;
    this.toStartDate = updatedToDate;

    this.setMinMaxDateValues(updatedFromDate, searchCriteria);

    this.displayFromDate = this.dateFormatter.localizeDateToString(updatedFromDate, true);
    this.displayToDate = this.dateFormatter.localizeDateToString(updatedToDate, true);
  }

  private getFromDate(payload: InventoryManagementSearchCriteria): NgbDateStruct | undefined {
    if (payload.dateRange?.fromDate) {
      return this.dateFormatter.parse(payload.dateRange.fromDate);
    } else {
      if (payload.filterByStatus === InventoryManagementAuctionItemStatus.ACTIVE) {
        // If the user click on the ACTIVE radio button, the dates will be set to be undefined
        return undefined;
      } else {
        // If the user performs a blank search (clearing VIN/Stock# or clearing multiple VIN
        // then clicking Search button), the dates will be set to bring back the last 7 days of sold vehicles
        return this.ngbCalendar.getPrev(NgbDate.from(this.today), 'd', 7);
      }
    }
  }

  private getToDate(payload: InventoryManagementSearchCriteria): NgbDateStruct | undefined {
    if (payload.dateRange?.toDate) {
      return this.dateFormatter.parse(payload.dateRange.toDate);
    } else {
      // If the user performs a blank search (clearing VIN/Stock# or clearing multiple VIN
      // then clicking Search button), the dates will be set to bring back the last 7 days of sold vehicles
      return this.today;
    }
  }

  setInitialValues() {
    this.store
      .select(InventoryManagementState.searchCriteria)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {

        this.toMaxDate = this.today;
        this.fromMinDate = { year: this.today.year - 1, month: this.today.month, day: this.today.day };

        const existingCriteria = this.store.selectSnapshot(InventoryManagementState.searchCriteria) as InventoryManagementSearchCriteria;

        if (existingCriteria && existingCriteria.dateRange?.toDate) {
          const parsedToDate = this.dateFormatter.parse(existingCriteria.dateRange.toDate);
          this.toFormControl.setValue(parsedToDate, { emitEvent: false });
          this.toStartDate = parsedToDate;
          this.fromMaxDate = parsedToDate;
        } else {
          this.toFormControl.setValue(this.today, { emitEvent: false });
          this.toStartDate = this.today;
          this.fromMaxDate = this.today;
        }

        if (existingCriteria && existingCriteria.dateRange?.fromDate) {
          const parsedFromDate = this.dateFormatter.parse(existingCriteria.dateRange.fromDate);
          this.fromFormControl.setValue(parsedFromDate, { emitEvent: false });
          this.displayFromDate = this.dateFormatter.localizeDateToString(parsedFromDate, true);
          this.fromStartDate = parsedFromDate;
          this.toMinDate = parsedFromDate;
        } else {
          // By default, the dates will be set to bring back the last 7 days of sold vehicles
          let defaultFromDate = this.ngbCalendar.getPrev(NgbDate.from(this.today), 'd', 7);
          if (existingCriteria && existingCriteria.filterByStatus === InventoryManagementAuctionItemStatus.ACTIVE) {
            // By default, the dates will be set to undefined of sold vehicles
            defaultFromDate = undefined;
          }

          this.fromFormControl.setValue(defaultFromDate, { emitEvent: false });
          this.fromStartDate = defaultFromDate;
          this.toMinDate = this.fromMinDate;
        }

        this.calculateDates(existingCriteria);
      });
  }

  updateFromDate(fromDate: NgbDate | NgbDateStruct) {
    const fromValue = NgbDate.from(this.dateFormatter.sanitizeDateInput(fromDate));
    this.toMinDate = fromValue ? fromValue : this.fromMinDate;

    if (fromDate && this.validateFromValue()) {
      this.fromStartDate = fromValue ? fromValue : this.fromStartDate;
      this.displayFromDate = this.dateFormatter.localizeDateToString(this.fromStartDate, true);
      this.fromDateValid = true;
      this.toDateValid = this.validateToValue();

      const sanitizedToValue = this.dateFormatter.sanitizeDateInput(this.toFormControl.value);

      if (this.toDateValid && this.isValidDateRange()) {
        this.toDateValid = true;
        this.updateOfferDateFilter(fromValue, NgbDate.from(sanitizedToValue));
      }
    } else {
      this.fromDateValid = false;
    }
    this.hasDateRangeError.emit(!(this.fromDateValid && this.toDateValid));
  }

  updateToDate(toDate: NgbDate | NgbDateStruct) {
    const toValue = NgbDate.from(this.dateFormatter.sanitizeDateInput(toDate));

    this.fromMaxDate = toValue ? toValue : this.today;

    if (toDate && this.validateToValue()) {
      this.toStartDate = toValue;
      this.displayToDate = this.dateFormatter.localizeDateToString(this.toStartDate, true);
      this.toDateValid = true;
      this.fromDateValid = this.validateFromValue();

      const sanitizedFromValue = this.dateFormatter.sanitizeDateInput(this.fromFormControl.value);

      if (this.fromDateValid && this.isValidDateRange()) {
        this.fromDateValid = true;
        this.updateOfferDateFilter(NgbDate.from(sanitizedFromValue), toValue);
      }
    } else {
      this.toDateValid = false;
    }
    this.hasDateRangeError.emit(!(this.fromDateValid && this.toDateValid));
  }

  private isValidDateRange(): boolean {
    if (this.fromFormControl.value === undefined) return true;

    const toDate = new NgbDate(
      this.toFormControl.value.year,
      this.toFormControl.value.month,
      this.toFormControl.value.day
    );

    return toDate.after(this.fromFormControl.value) || toDate.equals(this.fromFormControl.value);
  }

  validateFromValue(): boolean {
    const toDateValue = this.dateFormatter.sanitizeDateInput(this.toFormControl.value);
    const fromDateValue = this.dateFormatter.sanitizeDateInput(this.fromFormControl.value);

    if (toDateValue === undefined && fromDateValue === undefined) return true;

    if (fromDateValue && fromDateValue.month !== null && fromDateValue.day !== null && fromDateValue.year !== null) {
      const fromDate = new NgbDate(fromDateValue.year, fromDateValue.month, fromDateValue.day);

      return (
        this.ngbCalendar.isValid(fromDate) &&
        (fromDate.after(this.fromMinDate) || fromDate.equals(this.fromMinDate)) &&
        (fromDate.before(this.fromMaxDate) || fromDate.equals(this.fromMaxDate))
      );
    } else {
      return false;
    }
  }

  validateToValue(): boolean {
    const toDateValue = this.dateFormatter.sanitizeDateInput(this.toFormControl.value);
    const fromDateValue = this.dateFormatter.sanitizeDateInput(this.fromFormControl.value);

    if (toDateValue === undefined && fromDateValue === undefined) return true;

    if (toDateValue && toDateValue.month !== null && toDateValue.day !== null && toDateValue.year !== null) {
      const toDate = new NgbDate(toDateValue.year, toDateValue.month, toDateValue.day);

      // if the from date is undefined (e.g. default from date for ACTIVE vehicles), just check if the to date is valid or undefined
      if (fromDateValue === undefined) return this.ngbCalendar.isValid(toDate);

      return (
        this.ngbCalendar.isValid(toDate) &&
        (toDate.after(this.toMinDate) || toDate.equals(this.toMinDate)) &&
        (toDate.before(this.toMaxDate) || toDate.equals(this.toMaxDate))
      );
    } else {
      return false;
    }
  }

  updateOfferDateFilter(fromDate: NgbDate, toDate: NgbDate) {
    const formattedFromDate = this.dateFormatter.formatPayload(fromDate);
    const formattedToDate = this.dateFormatter.formatPayload(toDate);

    this.displayFromDate = this.localizeDate(formattedFromDate, true);
    this.displayToDate = this.localizeDate(formattedToDate, true);

    this.searchFilterService.setDateRange(formattedFromDate, formattedToDate);
  }

  localizeDate(date: any, disableTimeZone?: boolean): string {
    return this.dateUtilsService.localizeDate(date, disableTimeZone);
  }

  focusFromCalendar() {
    this.fromCalendarButton().nativeElement.focus();
  }

  focusToCalendar() {
    this.toCalendarButton().nativeElement.focus();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.hasDateRangeError.emit(false);
  }
}
